import React, { createContext, useContext, ReactNode } from 'react';
import { useSessionManager } from '@/hooks/useSessionManager';
import SessionExpiryWarning from '@/components/ui/SessionExpiryWarning';
import {
  formStatePersistence,
  importWizardPersistence,
  navigationPersistence,
} from '@/utils/statePersistence';

// Session context type
interface SessionContextType {
  sessionState: {
    isActive: boolean;
    lastActivity: number;
    sessionStart: number;
    showWarning: boolean;
    isRefreshing: boolean;
    absoluteExpiry: number;
  };
  extendSession: () => void;
  logout: () => void;
  isSessionExpired: boolean;
  timeUntilIdleExpiry: number;
  timeUntilAbsoluteExpiry: number;
  // State persistence helpers
  saveFormState: (formId: string, data: Record<string, any>, route: string) => Promise<void>;
  getFormState: (formId: string) => Promise<any>;
  clearFormState: (formId: string) => Promise<void>;
  saveWizardState: (data: Record<string, any>, step: number, files?: File[]) => Promise<void>;
  getWizardState: () => Promise<any>;
  clearWizardState: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

interface SessionProviderProps {
  children: ReactNode;
  warningVariant?: 'banner' | 'modal';
  enableStatePersistence?: boolean;
}

export function SessionProvider({
  children,
  warningVariant = 'banner',
  enableStatePersistence = true,
}: SessionProviderProps) {
  const sessionManager = useSessionManager();

  // State persistence helpers
  const saveFormState = async (formId: string, data: Record<string, any>, route: string) => {
    if (!enableStatePersistence) return;
    await formStatePersistence.saveFormState(formId, data, route);
  };

  const getFormState = async (formId: string) => {
    if (!enableStatePersistence) return null;
    return await formStatePersistence.getFormState(formId);
  };

  const clearFormState = async (formId: string) => {
    if (!enableStatePersistence) return;
    await formStatePersistence.clearFormState(formId);
  };

  const saveWizardState = async (data: Record<string, any>, step: number, files?: File[]) => {
    if (!enableStatePersistence) return;
    await importWizardPersistence.saveWizardState(data, step, files);
  };

  const getWizardState = async () => {
    if (!enableStatePersistence) return null;
    return await importWizardPersistence.getWizardState();
  };

  const clearWizardState = async () => {
    if (!enableStatePersistence) return;
    await importWizardPersistence.clearWizardState();
  };

  // Handle extending session (dismiss warning and extend)
  const handleExtendSession = () => {
    sessionManager.extendSession();
  };

  // Handle dismissing warning (but keep session active)
  const handleDismissWarning = () => {
    sessionManager.extendSession(); // Extending session also dismisses warning
  };

  // Context value
  const contextValue: SessionContextType = {
    ...sessionManager,
    saveFormState,
    getFormState,
    clearFormState,
    saveWizardState,
    getWizardState,
    clearWizardState,
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {children}

      {/* Session expiry warning component */}
      <SessionExpiryWarning
        show={sessionManager.sessionState.showWarning}
        timeUntilExpiry={sessionManager.timeUntilIdleExpiry}
        onExtend={handleExtendSession}
        onDismiss={handleDismissWarning}
        variant={warningVariant}
      />
    </SessionContext.Provider>
  );
}

// Hook to use session context
export function useSession(): SessionContextType {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

// Hook for form state persistence (can be used independently)
export function useFormStatePersistence(formId: string, route: string) {
  const session = useSession();

  const saveState = async (data: Record<string, any>) => {
    await session.saveFormState(formId, data, route);
  };

  const loadState = async () => {
    return await session.getFormState(formId);
  };

  const clearState = async () => {
    await session.clearFormState(formId);
  };

  return { saveState, loadState, clearState };
}

// Hook for import wizard state persistence
export function useImportWizardPersistence() {
  const session = useSession();

  const saveWizardState = async (data: Record<string, any>, step: number, files?: File[]) => {
    await session.saveWizardState(data, step, files);
  };

  const loadWizardState = async () => {
    return await session.getWizardState();
  };

  const clearWizardState = async () => {
    await session.clearWizardState();
  };

  return { saveWizardState, loadWizardState, clearWizardState };
}

// Export the provider and hooks
export default SessionProvider;
